import './styles/Checkbox.scss';

import { CheckSquare } from '@phosphor-icons/react';
import React, {
  ChangeEvent,
  HTMLAttributes,
  PropsWithChildren,
  useRef,
} from 'react';

import { cn } from '../../helpers/classBuilders';
import { useClassHelper } from '../../hooks';
import { Paragraph } from '../Typography';

const Variants = {
  default: 'default',
  primary: 'primary',
} as const;

interface CheckBoxProps
  extends HTMLAttributes<HTMLInputElement>,
    PropsWithChildren {
  label?: string; // TODO - Optional, this is pass in some components but never used in Checkbox - To be deleted later

  disabled?: boolean;
  isChecked: boolean;
  value?: string;
  name: string;
  variant?: keyof typeof Variants;
  verticalCentered?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  dataCy?: string;
}

const className = 'c-Checkbox';

export const Checkbox = ({
  id,
  children,
  onChange,
  isChecked,
  name,
  value,
  disabled = false,
  tabIndex = 0,
  variant = Variants.default,
  verticalCentered = false,
  dataCy = '',
  ...rest
}: CheckBoxProps) => {
  const c = useClassHelper(className);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const inputId = c(`-${name}`);

  /**
   * This function was done to handle keyboard interaction
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();

      if (inputRef) {
        inputRef.current.click();
      }
    }
  };

  /**
   * The only way to trigger input onChange from a button
   */
  const handleButtonClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div className={cn(className, verticalCentered && c('--v-centered'))}>
      <input
        className={c('__input')}
        id={inputId}
        name={name}
        defaultValue={value}
        type="checkbox"
        checked={isChecked}
        onChange={onChange}
        disabled={disabled}
        tabIndex={-1}
        aria-disabled={disabled ? 'true' : 'false'}
        ref={inputRef}
        {...rest}
      />
      <button
        type="button"
        role="checkbox"
        aria-checked={isChecked ? 'true' : 'false'}
        className={`${c(
          '__button',
          disabled ? '__button--disabled' : '',
          isChecked ? `__button-${variant}-checked` : '',
        )} focus-visible:focus-ring-form`}
        tabIndex={tabIndex}
        id={id}
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        aria-disabled={disabled ? 'true' : 'false'}
        data-cy={dataCy ? `${dataCy}` : undefined}
      >
        {isChecked ? (
          <CheckSquare weight="fill" size={24} aria-label="selected-icon" />
        ) : (
          <span className="icon" aria-label="empty-icon" />
        )}
      </button>
      <Paragraph
        tag="label"
        className={c(`__label`, {
          '__label--disabled': disabled,
        })}
        htmlFor={inputId}
      >
        {children}
      </Paragraph>
    </div>
  );
};
